/**
 * User-added companies API.
 * Base: /api/v1/users/{userId}/companies
 * User-added companies are created/edited by a user and visible only to that user.
 * Global companies are accessible to everyone.
 */
import { createApiResponse, createCompany } from '../models';
import { createUserApiClient } from './authenticatedApiClient';
import { getStoredUserId } from './authSession';
import {
  mapSupplierApiError,
  resolveSupplierUserId,
  toSupplierUpdatePatch,
  withUserScopedBody,
} from './supplierUserScope';

function getApiClient(userId) {
  return createUserApiClient(userId, 'companies');
}

function getUserId() {
  return getStoredUserId();
}

export const userCompaniesService = {
  /**
   * Create a user-added company (ticker unique per user).
   * POST /api/v1/users/{userId}/companies
   * Body: CompanyDTO shape (ticker, name required; description, industry recommended).
   */
  async createUserCompany(userId, body) {
    const uid = userId || getUserId();
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    try {
      const client = getApiClient(uid);
      const response = await client.post('/', body);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompany(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create company',
      });
    }
  },

  /**
   * List all companies added by this user.
   * GET /api/v1/users/{userId}/companies
   */
  async listUserCompanies(userId) {
    const uid = userId || getUserId();
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    try {
      const client = getApiClient(uid);
      const response = await client.get('/');
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map((c) => createCompany(c));
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch companies',
      });
    }
  },

  /**
   * Get one user-added company by ticker.
   * GET /api/v1/users/{userId}/companies/{ticker}
   */
  async getUserCompanyByTicker(userId, ticker) {
    const uid = userId || getUserId();
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    if (!ticker) {
      return createApiResponse({ success: false, message: 'Ticker is required' });
    }
    try {
      const client = getApiClient(uid);
      const response = await client.get(`/${encodeURIComponent(ticker)}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompany(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      if (error.response?.status === 404) {
        return createApiResponse({ success: false, message: 'Company not found', data: null });
      }
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company',
      });
    }
  },

  /**
   * Update user-added company.
   * PUT /api/v1/users/{userId}/companies/{ticker}
   */
  /**
   * PUT /api/v1/users/{userId}/companies/{ticker} — same user-scoped semantics as companies?ticker+userId.
   */
  async updateUserCompany(userId, ticker, body) {
    const uid = resolveSupplierUserId(userId || getUserId());
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    if (!ticker) {
      return createApiResponse({ success: false, message: 'Ticker is required' });
    }
    try {
      const client = getApiClient(uid);
      const patch = toSupplierUpdatePatch(body, ticker);
      const response = await client.put(
        `/${encodeURIComponent(patch.ticker || ticker)}`,
        withUserScopedBody(patch, uid)
      );
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createCompany(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return mapSupplierApiError(error, 'Failed to update company');
    }
  },

  /**
   * Delete user-added company (only for this user).
   * DELETE /api/v1/users/{userId}/companies/{ticker}
   */
  async deleteUserCompany(userId, ticker) {
    const uid = userId || getUserId();
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    if (!ticker) {
      return createApiResponse({ success: false, message: 'Ticker is required' });
    }
    try {
      const client = getApiClient(uid);
      const response = await client.delete(`/${encodeURIComponent(ticker)}`);
      return createApiResponse(response.data);
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete company',
      });
    }
  },

  /**
   * Batch-create user companies (max 200 per request; chunk larger arrays on the client).
   * POST /api/v1/users/{userId}/companies/batch — body: JSON array of CompanyDTO-shaped objects.
   */
  async batchUserCompanies(userId, companies) {
    const uid = userId || getUserId();
    if (!uid) {
      return createApiResponse({ success: false, message: 'User not authenticated' });
    }
    if (!Array.isArray(companies) || companies.length === 0) {
      return createApiResponse({ success: false, message: 'No suppliers to import' });
    }
    try {
      const client = getApiClient(uid);
      const response = await client.post('/batch', companies);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data?.created && Array.isArray(apiResponse.data.created)) {
        apiResponse.data = {
          ...apiResponse.data,
          created: apiResponse.data.created.map((c) => createCompany(c)),
        };
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Batch import failed',
      });
    }
  },

  getUserId,
};
